import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendEmailVerification,
    sendPasswordResetEmail,
    updateProfile,
    updatePassword,
    deleteUser,
    reauthenticateWithCredential,
    EmailAuthProvider,
    User,
    AuthError
} from 'firebase/auth';
import {
    doc,
    setDoc,
    getDoc,
    deleteDoc,
    serverTimestamp,
    updateDoc,
    arrayUnion,
    arrayRemove,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import {
    getDownloadURL,
    ref,
    uploadBytes,
    deleteObject
} from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import { DEFAULT_COMPANY_LOGO_PATH, DEFAULT_COMPANY_LOGO_URL } from '../constants/storage';
import { logger } from '../utils/logger';
import { getStrings } from '../constants/strings';
import { cancelSubscription } from './stripeService';

export interface AuthUser {
    uid: string;
    email: string | null;
    displayName: string | null;
    emailVerified: boolean;
}

export interface RegistrationData {
    nickname: string;
    email: string;
    password: string;
    organization?: string;
    role: string;
    selectedPlan?: string;
    language?: string;
    organizationLogoFile?: File | null;
    useDefaultLogo?: boolean;
}

export interface UserProfile {
    uid: string;
    email: string;
    displayName: string;
    role: string;
    organization?: string;
    selectedPlan?: string;
    language?: string;
    reportLanguage?: string;
    createdAt: any;
    emailVerified: boolean;
    // Note: editorLimit and userLimit are computed dynamically from selectedPlan
    // They are not stored in DB to avoid data duplication and sync issues
    // Note: currentEditors and currentUsers are computed dynamically from CompanyProfile
    // They are not stored in DB to avoid data duplication
    // Ultimate plan overrides: when selectedPlan is 'ultimate', these override plan defaults (set via Firebase Console for special offers)
    editorLimitOverride?: number;
    userLimitOverride?: number;
    toolLimitOverride?: number | 'unlimited';
    // Subscription fields
    subscriptionStatus?: 'active' | 'cancelled' | 'expired' | 'past_due' | 'incomplete' | null;
    subscriptionId?: string | null;
    subscriptionEndDate?: any | null;
}

export interface AuthErrorInfo {
    code: string;
    message: string;
}

// Convert Firebase User to our AuthUser interface
const convertFirebaseUser = (user: User): AuthUser => ({
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified
});

// Convert Firebase AuthError to our AuthErrorInfo interface
const convertFirebaseError = (error: AuthError): AuthErrorInfo => ({
    code: error.code,
    message: error.message
});

// Unified tool interface for both standard and custom tools
export interface Tool {
    id: string; // Unique ID: "standard-{toolNumber}" for standard tools, unique ID for custom tools
    type: 'standard' | 'custom'; // Tool type
    name: string; // Tool name
    imageUrl: string | null; // Image URL (path for standard tools, uploaded URL for custom tools)
    color: string; // Tool color
    toolNumber?: number; // Standard tool number (1-12), only for standard tools
    toolWithoutMeasurement?: boolean; // Tool without measurement: for custom tools and standard tools (Caliper, Height Gauge, NO-GO, GO, Pin Gauge)
    precisionClass?: string; // Precision class for custom tools
    minToolPrecision?: string; // Minimum precision of tool (e.g., "0", "0.0", "0.00", "0.000")
    createdAt: any; // Creation date
}

// Legacy CustomTool interface for backward compatibility (will be removed)
export interface CustomTool {
    id: string;
    name: string;
    imageUrl?: string | null;
    color: string;
    createdAt: any;
}

// Local server (on-prem per company) discovery metadata.
// Frontend reads `url` from this object to talk to the customer's local server.
// `lastSeenAt` and `version` are written by the local server itself via the cloud
// backend heartbeat; the frontend only reads them for the manager status panel.
export type PdfWorkflowMode = 'manual' | 'localServer' | 'cloudDb';

export interface LocalServerInfo {
    url: string;
    enabled: boolean;
    lastSeenAt?: number | null;
    version?: string | null;
    publicKeyFingerprint?: string | null;
}

// Company profile interface
export interface CompanyProfile {
    companyId: string;
    managerId: string;
    companyName: string;
    selectedPlan?: string;
    editors: string[];
    users: string[];
    tools?: Tool[]; // Unified array of tools (both standard and custom)
    createdAt: any;
    updatedAt: any;
    logoUrl?: string | null;
    logoFileName?: string | null;
    reportLanguage?: string; // Report language for the organization
    localServer?: LocalServerInfo;
    pdfWorkflowMode?: PdfWorkflowMode;
    // Note: Subscription fields are stored only in UserProfile (manager document)
    // Company subscription status is always checked via managerId -> UserProfile
}

// Increment this when changing limits to invalidate dashboard cache
export const LIMITS_VERSION = 3;

// Override limits for Ultimate plan (set via Firebase Console for special offers)
export interface LimitOverrides {
    editorLimitOverride?: number;
    userLimitOverride?: number;
    toolLimitOverride?: number | 'unlimited';
}

// Get subscription limits based on plan. When plan is 'ultimate', overrides from user profile take precedence (set via Firebase Console).
export const getSubscriptionLimits = (
    plan?: string,
    overrides?: LimitOverrides
): { editorLimit: number; userLimit: number; toolLimit: number | 'unlimited' } => {
    let limits: { editorLimit: number; userLimit: number; toolLimit: number | 'unlimited' };
    switch (plan?.toLowerCase()) {
        case 'basic':
            limits = { editorLimit: 1, userLimit: 5, toolLimit: 5 };
            break;
        case 'premium':
            limits = { editorLimit: 5, userLimit: 30, toolLimit: 30 };
            break;
        case 'ultimate':
            limits = { editorLimit: 100, userLimit: 300, toolLimit: 'unlimited' };
            if (overrides) {
                const parsePositiveLimit = (val: unknown): number | null => {
                    if (val === undefined || val === null) return null;
                    const num = typeof val === 'number' ? val : parseInt(String(val), 10);
                    return !isNaN(num) && num > 0 ? num : null;
                };
                const editorVal = parsePositiveLimit(overrides.editorLimitOverride);
                if (editorVal !== null) limits.editorLimit = editorVal;
                const userVal = parsePositiveLimit(overrides.userLimitOverride);
                if (userVal !== null) limits.userLimit = userVal;
                if (overrides.toolLimitOverride !== undefined) {
                    limits.toolLimit = overrides.toolLimitOverride;
                }
            }
            break;
        default:
            limits = { editorLimit: 1, userLimit: 5, toolLimit: 5 };
    }
    return limits;
};

/**
 * Premium / ultimate org plans (not basic). Used for Gemini Vision and standard-based tolerance automation.
 */
export const isPremiumOrUltimatePlan = (plan?: string | null): boolean => {
    const p = plan?.toLowerCase().trim();
    return p === 'premium' || p === 'ultimate';
};

export const isGeminiVisionAllowedForPlan = isPremiumOrUltimatePlan;

// Get plan price for comparison
const getPlanPrice = (plan?: string): number => {
    switch (plan?.toLowerCase()) {
        case 'basic':
            return 60;
        case 'premium':
            return 240;
        case 'ultimate':
            return 1000; // Ultimate is always the most expensive
        default:
            return 0;
    }
};

// Compare plans - returns true if newPlan is cheaper than currentPlan
export const isPlanDowngrade = (currentPlan: string | null | undefined, newPlan: string): boolean => {
    if (!currentPlan) return false;
    const currentPrice = getPlanPrice(currentPlan);
    const newPrice = getPlanPrice(newPlan);
    return newPrice < currentPrice;
};

// Remove all editors and users from company (used when downgrading plan)
// Also clears organization field (companyId) for all removed members so they can be added again
export const removeAllCompanyMembers = async (managerId: string): Promise<{ editorsRemoved: number; usersRemoved: number }> => {
    try {
        const companyProfile = await getCompanyProfile(managerId);
        if (!companyProfile) {
            logger.warn('Company profile not found for removing members');
            return { editorsRemoved: 0, usersRemoved: 0 };
        }

        const editorsRemoved = companyProfile.editors.length;
        const usersRemoved = companyProfile.users.length;

        // Clear organization field (companyId) for all editors
        const editorUpdatePromises = companyProfile.editors.map(editorId =>
            updateUserOrganization(editorId, null).catch(error => {
                logger.warn(`Failed to clear organization (companyId) for editor ${editorId}:`, error);
                // Continue with other editors even if one fails
            })
        );

        // Clear organization field (companyId) for all users
        const userUpdatePromises = companyProfile.users.map(userId =>
            updateUserOrganization(userId, null).catch(error => {
                logger.warn(`Failed to clear organization (companyId) for user ${userId}:`, error);
                // Continue with other users even if one fails
            })
        );

        // Clear company arrays and update user organizations in parallel
        const companyDocRef = doc(db, 'companies', managerId);
        await Promise.all([
            updateDoc(companyDocRef, {
                editors: [],
                users: [],
                updatedAt: serverTimestamp()
            }),
            ...editorUpdatePromises,
            ...userUpdatePromises
        ]);

        // Note: currentEditors and currentUsers are computed dynamically from CompanyProfile
        // No need to update them in user profile

        logger.log(`Removed all company members: ${editorsRemoved} editors, ${usersRemoved} users. Cleared organization field for all removed members.`);
        return { editorsRemoved, usersRemoved };
    } catch (error) {
        logger.error('Error removing all company members:', error);
        throw error;
    }
};

// Update subscription plan in user and company profiles
// Note: Limits are computed dynamically from plan, no need to store them
export const updateSubscriptionPlan = async (managerId: string, planId: string): Promise<void> => {
    try {
        // Update user profile with new plan
        const userDocRef = doc(db, 'users', managerId);
        await updateDoc(userDocRef, {
            selectedPlan: planId,
            updatedAt: serverTimestamp()
        });

        // Update company profile if user is a manager
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);
        if (companyDoc.exists()) {
            await updateDoc(companyDocRef, {
                selectedPlan: planId,
                updatedAt: serverTimestamp()
            });
        }

        const limits = getSubscriptionLimits(planId);
        logger.log(`Updated subscription plan for manager ${managerId}: plan=${planId}, editors=${limits.editorLimit}, users=${limits.userLimit}`);
    } catch (error) {
        logger.error('Error updating subscription plan:', error);
        throw error;
    }
};

// Save user profile data to Firestore
// Note: Limits are computed dynamically from selectedPlan, not stored in DB
export const saveUserProfile = async (userData: UserProfile): Promise<void> => {
    try {
        const userDocRef = doc(db, 'users', userData.uid);

        await setDoc(userDocRef, {
            uid: userData.uid,
            email: userData.email,
            displayName: userData.displayName,
            role: userData.role,
            organization: userData.organization || null,
            selectedPlan: userData.selectedPlan || null,
            language: userData.language || 'en',
            reportLanguage: userData.reportLanguage || userData.language || 'en',
            createdAt: serverTimestamp(),
            emailVerified: userData.emailVerified,
            // Note: currentEditors and currentUsers are computed dynamically from CompanyProfile
            subscriptionStatus: userData.subscriptionStatus || null,
            subscriptionId: userData.subscriptionId || null,
            subscriptionEndDate: userData.subscriptionEndDate || null,
            updatedAt: serverTimestamp()
        });
        logger.log('User profile saved to Firestore successfully');
    } catch (error) {
        logger.error('Error saving user profile to Firestore:', error);
        throw error;
    }
};

// Save company profile data to Firestore (for managers only)
export const saveCompanyProfile = async (companyData: CompanyProfile): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', companyData.managerId);

        await setDoc(companyDocRef, {
            companyId: companyData.managerId,
            managerId: companyData.managerId,
            companyName: companyData.companyName,
            selectedPlan: companyData.selectedPlan || null,
            editors: [],
            users: [],
            tools: companyData.tools || [],
            logoUrl: companyData.logoUrl || null,
            logoFileName: companyData.logoFileName || null,
            // Note: Subscription fields are stored only in UserProfile (manager document)
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        logger.log('Company profile saved to Firestore successfully');
    } catch (error) {
        logger.error('Error saving company profile to Firestore:', error);
        throw error;
    }
};

// Upload company logo to Firebase Storage user folder and return URL and file name
const uploadCompanyLogo = async (managerId: string, file: File): Promise<{ url: string; fileName: string }> => {
    const fileName = file.name;
    // Store logo in user-specific folder to match Firebase Storage security rules:
    // path: user-uploads/{managerId}/{fileName}
    const storageRef = ref(storage, `user-uploads/${managerId}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    return { url, fileName };
};

// Upload custom tool image to Firebase Storage
// Path: company-tools/{managerId}/{toolId}/image.{ext}
export const uploadCustomToolImage = async (managerId: string, toolId: string, file: File): Promise<string> => {
    try {
        // Get file extension
        const fileExtension = file.name.split('.').pop() || 'png';
        const fileName = `image.${fileExtension}`;

        // Store in company-tools folder: company-tools/{managerId}/{toolId}/image.{ext}
        const storageRef = ref(storage, `company-tools/${managerId}/${toolId}/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);

        logger.log(`Custom tool image uploaded: company-tools/${managerId}/${toolId}/${fileName}`);
        return url;
    } catch (error) {
        logger.error('Error uploading custom tool image:', error);
        throw error;
    }
};

// Delete custom tool image from Firebase Storage
export const deleteCustomToolImage = async (managerId: string, toolId: string, imageUrl: string): Promise<void> => {
    try {
        // Extract path from URL or construct it
        // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
        // We need to extract the path or construct it from managerId and toolId

        // Try to extract path from URL
        let imagePath = '';
        try {
            const urlObj = new URL(imageUrl);
            const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
            if (pathMatch) {
                imagePath = decodeURIComponent(pathMatch[ 1 ]);
            } else {
                // Fallback: construct path from managerId and toolId
                // Try to get file extension from URL
                const urlExtension = imageUrl.split('.').pop()?.split('?')[ 0 ] || 'png';
                imagePath = `company-tools/${managerId}/${toolId}/image.${urlExtension}`;
            }
        } catch {
            // If URL parsing fails, construct path
            imagePath = `company-tools/${managerId}/${toolId}/image.png`;
        }

        const storageRef = ref(storage, imagePath);
        await deleteObject(storageRef);
        logger.log(`Custom tool image deleted: ${imagePath}`);
    } catch (error: any) {
        // If file doesn't exist, it's okay - just log and continue
        if (error.code === 'storage/object-not-found') {
            logger.log(`Custom tool image not found, skipping deletion`);
        } else {
            logger.error('Error deleting custom tool image:', error);
            // Don't throw - image deletion failure shouldn't break the flow
        }
    }
};

// Register a new user with email and password
export const registerUser = async (data: RegistrationData): Promise<AuthUser> => {
    try {
        // Create user with email and password
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            data.email,
            data.password
        );

        const user = userCredential.user;

        // Update user profile with display name
        await updateProfile(user, {
            displayName: data.nickname
        });

        // Send email verification
        await sendEmailVerification(user);

        // Optional: set or upload organization logo for manager
        let companyLogoUrl: string | undefined;
        let companyLogoFileName: string | undefined;
        if (data.role === 'manager') {
            // Case 1: manager wants to use default logo from public storage folder
            if (data.useDefaultLogo) {
                // Use local public URL to avoid Firebase Storage token URLs and extra paid requests
                companyLogoUrl = DEFAULT_COMPANY_LOGO_URL;
                companyLogoFileName = DEFAULT_COMPANY_LOGO_URL;
                logger.log('[Registration] Default logo URL set to local public URL:', companyLogoUrl);
                // Case 2: custom logo uploaded by manager
            } else if (data.organizationLogoFile) {
                try {
                    const uploadResult = await uploadCompanyLogo(user.uid, data.organizationLogoFile);
                    companyLogoUrl = uploadResult.url;
                    companyLogoFileName = uploadResult.fileName;
                    logger.log('[Registration] Custom logo uploaded successfully:', companyLogoUrl);
                } catch (uploadError) {
                    // Log error but do not fail whole registration if logo upload fails
                    logger.error('[Registration] Error uploading company logo:', uploadError);
                }
            }
        }

        // Save user profile data to Firestore
        // For managers: organization field stores companyId (managerId)
        // For editors/users: organization field stores companyId (managerId of their company)
        // Normalize email to lowercase for consistent storage
        const normalizedEmail = (user.email || data.email).toLowerCase().trim();
        const userProfile: UserProfile = {
            uid: user.uid,
            email: normalizedEmail,
            displayName: data.nickname,
            role: data.role,
            organization: data.role === 'manager' ? user.uid : undefined, // Store companyId for manager
            selectedPlan: data.selectedPlan,
            language: data.language,
            createdAt: serverTimestamp(),
            emailVerified: user.emailVerified,
            // Subscription fields: set to null for new users (no subscription yet)
            // These will be updated by Stripe webhooks when subscription is created
            subscriptionStatus: null,
            subscriptionId: null,
            subscriptionEndDate: null
        };

        await saveUserProfile(userProfile);

        // If user is a manager, create company profile
        if (data.role === 'manager' && data.organization) {
            const companyProfile: CompanyProfile = {
                companyId: user.uid,
                managerId: user.uid,
                companyName: data.organization,
                selectedPlan: data.selectedPlan,
                editors: [],
                users: [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                logoUrl: companyLogoUrl || null,
                logoFileName: companyLogoFileName || null
                // Note: Subscription fields are stored only in UserProfile (manager document)
            };

            await saveCompanyProfile(companyProfile);
        }

        return convertFirebaseUser(user);
    } catch (error) {
        throw convertFirebaseError(error as AuthError);
    }
};

// Sign in user with email and password
export const signInUser = async (email: string, password: string): Promise<AuthUser> => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return convertFirebaseUser(userCredential.user);
    } catch (error) {
        throw convertFirebaseError(error as AuthError);
    }
};

// Sign out current user
export const signOutUser = async (): Promise<void> => {
    try {
        await signOut(auth);
    } catch (error) {
        throw convertFirebaseError(error as AuthError);
    }
};

// Send password reset email
export const sendPasswordReset = async (email: string): Promise<void> => {
    try {
        await sendPasswordResetEmail(auth, email);
        logger.log('Password reset email sent successfully');
    } catch (error) {
        logger.error('Error sending password reset email:', error);
        throw convertFirebaseError(error as AuthError);
    }
};

// Get current user
export const getCurrentUser = (): AuthUser | null => {
    const user = auth.currentUser;
    return user ? convertFirebaseUser(user) : null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
    return auth.currentUser !== null;
};

function backendApiUrl(path: string): string {
    const base = (process.env['REACT_APP_BACKEND_URL'] || 'http://localhost:3002').replace(/\/+$/, '');
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${base}/api/${cleanPath}`;
}

/**
 * Mirror Firestore organization/role into Firebase ID token custom claims (companyId, role).
 * Required for on-prem local server tenant checks. Refreshes the ID token when forceRefresh is true.
 */
export const syncAuthTokenClaims = async (forceRefresh = true): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
        return;
    }

    const token = await user.getIdToken(false);
    const response = await fetch(backendApiUrl('auth/sync-claims'), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        logger.warn('[authService] Failed to sync auth token claims', { status: response.status });
        return;
    }

    if (forceRefresh) {
        await user.getIdToken(true);
    }
};

/**
 * Manager: refresh custom claims for a company member (after add/remove in dashboard).
 */
export const syncAuthTokenClaimsForUser = async (userId: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
        return;
    }

    const token = await user.getIdToken(false);
    const response = await fetch(backendApiUrl(`auth/sync-claims/${encodeURIComponent(userId)}`), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        logger.warn('[authService] Failed to sync claims for user', { userId, status: response.status });
        throw new Error('Failed to sync user claims');
    }
};

// Get user profile from Firestore
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
        const userDocRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            return userDoc.data() as UserProfile;
        }

        logger.log('User profile not found in Firestore');
        return null;
    } catch (error) {
        logger.error('Error fetching user profile from Firestore:', error);
        throw error;
    }
};

// Get company profile from Firestore
export const getCompanyProfile = async (managerId: string): Promise<CompanyProfile | null> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (companyDoc.exists()) {
            return companyDoc.data() as CompanyProfile;
        }

        logger.log('Company profile not found in Firestore');
        return null;
    } catch (error) {
        logger.error('Error fetching company profile from Firestore:', error);
        throw error;
    }
};

// Get company profile for any user (manager, editor or user)
export const getCompanyProfileForUser = async (userId: string): Promise<CompanyProfile | null> => {
    try {
        const companiesRef = collection(db, 'companies');

        // 1) If user is a manager of a company (managerId == userId)
        let q = query(companiesRef, where('managerId', '==', userId));
        let querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            return querySnapshot.docs[ 0 ].data() as CompanyProfile;
        }

        // 2) Look for user in editors array
        q = query(companiesRef, where('editors', 'array-contains', userId));
        querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            return querySnapshot.docs[ 0 ].data() as CompanyProfile;
        }

        // 3) Look for user in users array
        q = query(companiesRef, where('users', 'array-contains', userId));
        querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            return querySnapshot.docs[ 0 ].data() as CompanyProfile;
        }

        logger.log('Company profile for user not found in Firestore');
        return null;
    } catch (error) {
        logger.error('Error fetching company profile for user from Firestore:', error);
        throw error;
    }
};

// Get user by email from Firestore
export const getUserByEmail = async (email: string): Promise<UserProfile | null> => {
    try {
        // Normalize email to lowercase for case-insensitive search
        const normalizedEmail = email.toLowerCase().trim();
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', normalizedEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[ 0 ];
            return userDoc.data() as UserProfile;
        }

        return null;
    } catch (error) {
        logger.error('Error fetching user by email from Firestore:', error);
        throw error;
    }
};

// Check if user is already in company
export const isUserInCompany = async (managerId: string, userEmail: string): Promise<boolean> => {
    try {
        // Normalize email to lowercase for case-insensitive search
        const normalizedEmail = userEmail.toLowerCase().trim();
        const companyProfile = await getCompanyProfile(managerId);
        if (!companyProfile) {
            return false;
        }

        const userProfile = await getUserByEmail(normalizedEmail);
        if (!userProfile) {
            return false;
        }

        // Check if user is in editors or users array
        return companyProfile.editors.includes(userProfile.uid) ||
            companyProfile.users.includes(userProfile.uid);
    } catch (error) {
        logger.error('Error checking if user is in company:', error);
        throw error;
    }
};

// Get all company members (editors and users)
export interface CompanyMember {
    uid: string;
    email: string;
    displayName: string;
    role: 'editor' | 'user';
}

export const getCompanyMembers = async (managerId: string): Promise<CompanyMember[]> => {
    try {
        const companyProfile = await getCompanyProfile(managerId);
        if (!companyProfile) {
            logger.log('Company profile not found');
            return [];
        }

        const members: CompanyMember[] = [];
        const invalidEditorIds: string[] = [];
        const invalidUserIds: string[] = [];

        // Load all editors
        for (const editorId of companyProfile.editors) {
            const userProfile = await getUserProfile(editorId);
            if (userProfile) {
                members.push({
                    uid: userProfile.uid,
                    email: userProfile.email,
                    displayName: userProfile.displayName,
                    role: 'editor'
                });
            } else {
                // User doesn't exist, mark for removal
                invalidEditorIds.push(editorId);
                logger.warn(`Invalid editor ID found in company: ${editorId}`);
            }
        }

        // Load all users
        for (const userId of companyProfile.users) {
            const userProfile = await getUserProfile(userId);
            if (userProfile) {
                members.push({
                    uid: userProfile.uid,
                    email: userProfile.email,
                    displayName: userProfile.displayName,
                    role: 'user'
                });
            } else {
                // User doesn't exist, mark for removal
                invalidUserIds.push(userId);
                logger.warn(`Invalid user ID found in company: ${userId}`);
            }
        }

        // Clean up invalid IDs from company document
        if (invalidEditorIds.length > 0 || invalidUserIds.length > 0) {
            await cleanupInvalidCompanyMembers(managerId, invalidEditorIds, invalidUserIds);
        }

        logger.log('Loaded company members:', members.length);
        return members;
    } catch (error) {
        logger.error('Error getting company members:', error);
        throw error;
    }
};

// Clean up invalid user IDs from company arrays
export const cleanupInvalidCompanyMembers = async (
    managerId: string,
    invalidEditorIds: string[],
    invalidUserIds: string[]
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            logger.log('Company document not found for cleanup');
            return;
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const updates: any = {
            updatedAt: serverTimestamp()
        };

        // Filter out invalid editor IDs
        if (invalidEditorIds.length > 0 && companyData.editors) {
            const validEditors = companyData.editors.filter(
                (editorId: string) => !invalidEditorIds.includes(editorId)
            );
            updates.editors = validEditors;
        }

        // Filter out invalid user IDs
        if (invalidUserIds.length > 0 && companyData.users) {
            const validUsers = companyData.users.filter(
                (userId: string) => !invalidUserIds.includes(userId)
            );
            updates.users = validUsers;
        }

        // Only update if there are changes
        if (invalidEditorIds.length > 0 || invalidUserIds.length > 0) {
            await updateDoc(companyDocRef, updates);
            logger.log(`Cleaned up ${invalidEditorIds.length} invalid editors and ${invalidUserIds.length} invalid users from company ${managerId}`);
        }
    } catch (error) {
        logger.error('Error cleaning up invalid company members:', error);
        // Don't throw - this is a cleanup operation, shouldn't break the main flow
    }
};

// Clean up all invalid user IDs from all companies (admin utility function)
export const cleanupAllInvalidCompanyMembers = async (): Promise<{ cleaned: number; errors: number }> => {
    let cleaned = 0;
    let errors = 0;

    try {
        const companiesRef = collection(db, 'companies');
        const companiesSnapshot = await getDocs(companiesRef);

        logger.log(`Starting cleanup for ${companiesSnapshot.size} companies...`);

        for (const companyDoc of companiesSnapshot.docs) {
            try {
                const companyData = companyDoc.data() as CompanyProfile;
                const managerId = companyDoc.id;
                const invalidEditorIds: string[] = [];
                const invalidUserIds: string[] = [];

                // Check editors
                if (companyData.editors && Array.isArray(companyData.editors)) {
                    for (const editorId of companyData.editors) {
                        const userProfile = await getUserProfile(editorId);
                        if (!userProfile) {
                            invalidEditorIds.push(editorId);
                        }
                    }
                }

                // Check users
                if (companyData.users && Array.isArray(companyData.users)) {
                    for (const userId of companyData.users) {
                        const userProfile = await getUserProfile(userId);
                        if (!userProfile) {
                            invalidUserIds.push(userId);
                        }
                    }
                }

                // Clean up if there are invalid IDs
                if (invalidEditorIds.length > 0 || invalidUserIds.length > 0) {
                    await cleanupInvalidCompanyMembers(managerId, invalidEditorIds, invalidUserIds);
                    cleaned++;
                    logger.log(`Cleaned company ${managerId}: ${invalidEditorIds.length} editors, ${invalidUserIds.length} users`);
                }
            } catch (error) {
                logger.error(`Error cleaning company ${companyDoc.id}:`, error);
                errors++;
            }
        }

        logger.log(`Cleanup completed: ${cleaned} companies cleaned, ${errors} errors`);
        return { cleaned, errors };
    } catch (error) {
        logger.error('Error in global cleanup:', error);
        throw error;
    }
};

// Add editor to company
export const addEditorToCompany = async (managerId: string, editorId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            editors: arrayUnion(editorId),
            updatedAt: serverTimestamp()
        });
        logger.log('Editor added to company successfully');
    } catch (error) {
        logger.error('Error adding editor to company:', error);
        throw error;
    }
};

// Remove editor from company
export const removeEditorFromCompany = async (managerId: string, editorId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            editors: arrayRemove(editorId),
            updatedAt: serverTimestamp()
        });
        logger.log('Editor removed from company successfully');
    } catch (error) {
        logger.error('Error removing editor from company:', error);
        throw error;
    }
};

// Add user to company
export const addUserToCompany = async (managerId: string, userId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            users: arrayUnion(userId),
            updatedAt: serverTimestamp()
        });
        logger.log('User added to company successfully');
    } catch (error) {
        logger.error('Error adding user to company:', error);
        throw error;
    }
};

// Remove user from company
export const removeUserFromCompany = async (managerId: string, userId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            users: arrayRemove(userId),
            updatedAt: serverTimestamp()
        });
        logger.log('User removed from company successfully');
    } catch (error) {
        logger.error('Error removing user from company:', error);
        throw error;
    }
};

// Helper functions to get standard tool name and image path
const getStandardToolName = (toolNumber: number): string => {
    // Use English strings as default (universal language)
    const strings = getStrings('en');
    const toolNames: { [ key: number ]: string } = {
        1: strings.CALIBER_TOOL,
        2: strings.MICROMETER,
        3: strings.DIGITAL_INSIDE_MICROMETER,
        4: strings.STOPWATCH,
        5: strings.HEIGHT_MEASURING_JACK,
        6: strings.SCREW_DRIVER,
        7: strings.TOOL_7,
        8: strings.TOOL_8,
        9: strings.TOOL_9,
        10: strings.TOOL_10,
        11: strings.TOOL_11,
        12: strings.MULTIMETER
    };
    return toolNames[ toolNumber ] || `Tool ${toolNumber}`;
};

const getStandardToolImagePath = (toolNumber: number): string => {
    const imagePaths: { [ key: number ]: string } = {
        1: '/assets/measurement-tools/6.png',
        2: '/assets/measurement-tools/5.png',
        3: '/assets/measurement-tools/1.png',
        4: '/assets/measurement-tools/2.png',
        5: '/assets/measurement-tools/3.png',
        6: '/assets/measurement-tools/4.png',
        7: '/assets/measurement-tools/7.png',
        8: '/assets/measurement-tools/8.png',
        9: '/assets/measurement-tools/9.png',
        10: '/assets/measurement-tools/10.png',
        11: '/assets/measurement-tools/11.png',
        12: '/assets/measurement-tools/multimeter.png'
    };
    return imagePaths[ toolNumber ] || '';
};

const getStandardToolDefaultColor = (toolNumber: number): string => {
    const legendColors = [
        '#2563EB', // 1 - blue
        '#DC2626', // 2 - red
        '#16A34A', // 3 - green
        '#7C3AED', // 4 - purple
        '#EA580C', // 5 - orange
        '#EAB308', // 6 - yellow
    ];
    return legendColors[ (toolNumber - 1) % legendColors.length ];
};

// Get default precision class for standard tool
export const getStandardToolDefaultPrecisionClass = (toolNumber: number): string => {
    // Default precision class values for all tools (including non-measurement tools)
    const precisionClasses: { [ key: number ]: string } = {
        1: '0.05',   // Caliper
        2: '0.002',  // Micrometer
        3: '0.002',  // Inside Micrometer
        4: '0.0015', // Dial Indicator (Stopwatch)
        5: '0.02',  // Height Gauge
        6: '6H',     // Thread Gauge (non-measurement tool)
        7: '6g',     // NO-GO Thread Ring Gauge (non-measurement tool)
        8: '6g',     // GO Thread Ring Gauge (non-measurement tool)
        9: '0.004',  // 3 Point Inside Bore Micrometer
        10: '0.04',  // Depth Micrometer
        11: '0.005', // Pin Gauge (non-measurement tool)
        12: '0'      // Multimeter (default value, not specified by user)
    };
    return precisionClasses[ toolNumber ] || '0.01';
};

// Get default minimum tool precision for standard tools
export const getStandardToolDefaultMinPrecision = (toolNumber: number): string | undefined => {
    // Default minimum precision values for standard tools
    const minPrecisions: { [ key: number ]: string } = {
        1: '0.00',   // Caliper
        2: '0.000',  // Micrometer
        3: '0.000',  // Inside Micrometer
        4: '0.000',  // Dial Indicator (Stopwatch)
        5: '0.00',   // Height Gauge
        9: '0.000',  // 3 Point Inside Bore Micrometer
        10: '0.00',  // Depth Micrometer
        12: '0'      // Multimeter
    };
    return minPrecisions[ toolNumber ];
};

// Add standard tool to company
export const addToolToCompany = async (
    managerId: string,
    toolNumber: number,
    precisionClass?: string,
    color?: string
): Promise<void> => {
    try {
        if (toolNumber < 1 || toolNumber > 12) {
            throw new Error('Invalid tool number. Must be between 1 and 12');
        }

        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const currentTools = companyData.tools || [];

        // Check if tool already exists
        const toolId = `standard-${toolNumber}`;
        if (currentTools.some(tool => tool.id === toolId)) {
            throw new Error('Tool already exists in company');
        }

        // Tools without measurement: Thread Gauge (6), NO-GO Thread Ring Gauge (7), GO Thread Ring Gauge (8), Pin Gauge (11)
        const toolsWithoutMeasurement = [ 6, 7, 8, 11 ];
        const isToolWithoutMeasurement = toolsWithoutMeasurement.includes(toolNumber);

        // Determine precisionClass value
        // For all tools (including non-measurement tools): use provided value or default precision class
        let finalPrecisionClass: string;
        finalPrecisionClass = (precisionClass && precisionClass.trim() !== '')
            ? precisionClass.trim()
            : getStandardToolDefaultPrecisionClass(toolNumber);

        const defaultMinPrecision = getStandardToolDefaultMinPrecision(toolNumber) || '';

        const newTool: Tool = {
            id: toolId,
            type: 'standard',
            name: getStandardToolName(toolNumber),
            imageUrl: getStandardToolImagePath(toolNumber),
            color: color || getStandardToolDefaultColor(toolNumber),
            toolNumber: toolNumber,
            toolWithoutMeasurement: isToolWithoutMeasurement,
            precisionClass: finalPrecisionClass,
            minToolPrecision: defaultMinPrecision,
            createdAt: new Date()
        };

        await updateDoc(companyDocRef, {
            tools: arrayUnion(newTool),
            updatedAt: serverTimestamp()
        });
        logger.log(`Standard tool ${toolNumber} added to company successfully`);
    } catch (error) {
        logger.error('Error adding tool to company:', error);
        throw error;
    }
};

// Add custom tool to company
export const addCustomToolToCompany = async (
    managerId: string,
    toolName: string,
    imageFile?: File | null,
    imageUrl?: string | null,
    color?: string,
    toolWithoutMeasurement?: boolean,
    precisionClass?: string,
    minToolPrecision?: string
): Promise<void> => {
    try {
        if (!toolName || toolName.trim() === '') {
            throw new Error('Tool name is required');
        }

        if (!color || color.trim() === '') {
            throw new Error('Tool color is required');
        }

        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const currentTools = companyData.tools || [];

        // Check if tool with same name already exists
        if (currentTools.some(tool => tool.name.toLowerCase().trim() === toolName.toLowerCase().trim())) {
            throw new Error('Tool with this name already exists');
        }

        // Generate unique ID for custom tool
        const toolId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        let finalImageUrl: string | null = null;

        // Upload image file if provided
        if (imageFile) {
            finalImageUrl = await uploadCustomToolImage(managerId, toolId, imageFile);
        } else if (imageUrl && imageUrl.trim() !== '') {
            // Use provided URL (for external URLs)
            finalImageUrl = imageUrl.trim();
        }

        const newTool: Tool = {
            id: toolId,
            type: 'custom',
            name: toolName.trim(),
            imageUrl: finalImageUrl,
            color: color,
            toolWithoutMeasurement: toolWithoutMeasurement || false,
            ...(precisionClass?.trim() && { precisionClass: precisionClass.trim() }),
            ...(minToolPrecision?.trim() && { minToolPrecision: minToolPrecision.trim() }),
            createdAt: new Date()
        };

        await updateDoc(companyDocRef, {
            tools: arrayUnion(newTool),
            updatedAt: serverTimestamp()
        });
        logger.log(`Custom tool "${toolName}" added to company successfully`);
    } catch (error) {
        logger.error('Error adding custom tool to company:', error);
        throw error;
    }
};

// Update tool color (works for both standard and custom tools)
export const updateToolColor = async (
    managerId: string,
    toolId: string,
    newColor: string
): Promise<void> => {
    try {
        if (!newColor || newColor.trim() === '') {
            throw new Error('Tool color is required');
        }

        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const tools = companyData.tools || [];

        // Find the tool to update
        const toolIndex = tools.findIndex(tool => tool.id === toolId);

        if (toolIndex === -1) {
            throw new Error('Tool not found');
        }

        // Create updated tool array
        const updatedTools = [ ...tools ];
        updatedTools[ toolIndex ] = {
            ...updatedTools[ toolIndex ],
            color: newColor.trim()
        };

        // Update Firestore
        await updateDoc(companyDocRef, {
            tools: updatedTools,
            updatedAt: serverTimestamp()
        });

        logger.log(`Tool color updated successfully`);
    } catch (error) {
        logger.error('Error updating tool color:', error);
        throw error;
    }
};

// Update tool minimum precision
export const updateToolMinPrecision = async (
    managerId: string,
    toolId: string,
    minPrecision: string | undefined
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const tools = companyData.tools || [];

        // Find the tool to update
        const toolIndex = tools.findIndex(tool => tool.id === toolId);

        if (toolIndex === -1) {
            throw new Error('Tool not found');
        }

        // Create updated tool array
        const updatedTools = [ ...tools ];
        updatedTools[ toolIndex ] = {
            ...updatedTools[ toolIndex ],
            minToolPrecision: minPrecision
        };

        // Update Firestore
        await updateDoc(companyDocRef, {
            tools: updatedTools,
            updatedAt: serverTimestamp()
        });

        logger.log(`Tool minimum precision updated successfully`);
    } catch (error) {
        logger.error('Error updating tool minimum precision:', error);
        throw error;
    }
};

// Remove tool from company and delete its image from Storage if custom
export const removeToolFromCompany = async (managerId: string, toolId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const tools = companyData.tools || [];

        // Find the tool to get its image URL
        const toolToRemove = tools.find(tool => tool.id === toolId);

        if (!toolToRemove) {
            throw new Error('Tool not found');
        }

        // Delete image from Storage if it's a custom tool and image exists
        if (toolToRemove.type === 'custom' && toolToRemove.imageUrl) {
            // Check if it's a Firebase Storage URL
            if (toolToRemove.imageUrl.includes('firebasestorage.googleapis.com')) {
                await deleteCustomToolImage(managerId, toolId, toolToRemove.imageUrl);
            }
        }

        // Remove tool from array by filtering it out (more reliable than arrayRemove)
        const updatedTools = tools.filter(tool => tool.id !== toolId);

        // Update Firestore with the filtered array
        await updateDoc(companyDocRef, {
            tools: updatedTools,
            updatedAt: serverTimestamp()
        });

        logger.log(`Tool "${toolToRemove.name}" removed from company successfully`);
    } catch (error) {
        logger.error('Error removing tool from company:', error);
        throw error;
    }
};

// Remove all tools from company
export const removeAllToolsFromCompany = async (managerId: string): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const companyDoc = await getDoc(companyDocRef);

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyProfile;
        const tools = companyData.tools || [];

        // Delete all custom tool images from Storage
        for (const tool of tools) {
            if (tool.type === 'custom' && tool.imageUrl && tool.imageUrl.includes('firebasestorage.googleapis.com')) {
                try {
                    await deleteCustomToolImage(managerId, tool.id, tool.imageUrl);
                } catch (error) {
                    // Continue even if image deletion fails
                    logger.warn(`Failed to delete image for tool ${tool.id}:`, error);
                }
            }
        }

        // Remove all tools from Firestore
        await updateDoc(companyDocRef, {
            tools: [],
            updatedAt: serverTimestamp()
        });

        logger.log('All tools removed from company successfully');
    } catch (error) {
        logger.error('Error removing all tools from company:', error);
        throw error;
    }
};

// Update user role in user profile
export const updateUserRole = async (userId: string, newRole: 'user' | 'editor'): Promise<void> => {
    try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
            role: newRole,
            updatedAt: serverTimestamp()
        });
        logger.log(`User role updated to ${newRole} for user ${userId}`);
    } catch (error) {
        logger.error('Error updating user role:', error);
        throw error;
    }
};

// Update user organization in user profile
// Note: organization field stores companyId (managerId), not company name
// Company name is stored in CompanyProfile.companyName and should be read from there
export const updateUserOrganization = async (
    userId: string,
    organization: string | null
): Promise<void> => {
    try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
            organization,
            updatedAt: serverTimestamp()
        });
        logger.log(`User organization (companyId) updated for user ${userId} to ${organization}`);

        try {
            await syncAuthTokenClaimsForUser(userId);
        } catch (syncError) {
            logger.warn('[authService] Claims sync after organization update failed', syncError);
        }
    } catch (error) {
        logger.error('Error updating user organization:', error);
        throw error;
    }
};

// Update company name in company profile
export const updateCompanyName = async (
    managerId: string,
    companyName: string
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            companyName,
            updatedAt: serverTimestamp()
        });
        logger.log(`Company name updated for manager ${managerId} to ${companyName}`);
    } catch (error) {
        logger.error('Error updating company name:', error);
        throw error;
    }
};

// Update report language in company profile
export const updateCompanyReportLanguage = async (
    managerId: string,
    reportLanguage: string
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            reportLanguage,
            updatedAt: serverTimestamp()
        });
        logger.log(`Company report language updated for manager ${managerId} to ${reportLanguage}`);
    } catch (error) {
        logger.error('Error updating company report language:', error);
        throw error;
    }
};

/**
 * Manager-only: write the local server URL/enabled flag for the company.
 * Health metadata (lastSeenAt, version, publicKeyFingerprint) is written by the
 * cloud backend on heartbeat — not by the client — so we never overwrite it here.
 */
export const updateCompanyLocalServer = async (
    managerId: string,
    url: string,
    enabled: boolean
): Promise<void> => {
    try {
        const trimmedUrl = url.trim();
        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            'localServer.url': trimmedUrl,
            'localServer.enabled': enabled,
            updatedAt: serverTimestamp()
        });
        logger.log(`Company local server updated for manager ${managerId}`);
    } catch (error) {
        logger.error('Error updating company local server:', error);
        throw error;
    }
};

export const updateCompanyPdfWorkflowMode = async (
    managerId: string,
    mode: PdfWorkflowMode
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);
        const updateData: Record<string, unknown> = {
            pdfWorkflowMode: mode,
            updatedAt: serverTimestamp()
        };
        if (mode === 'manual') {
            updateData['localServer.enabled'] = false;
        } else if (mode === 'localServer') {
            updateData['localServer.enabled'] = true;
        } else if (mode === 'cloudDb') {
            updateData['localServer.enabled'] = false;
        }
        await updateDoc(companyDocRef, updateData);
        logger.log(`Company PDF workflow mode updated to '${mode}' for manager ${managerId}`);
    } catch (error) {
        logger.error('Error updating company PDF workflow mode:', error);
        throw error;
    }
};

// Update company logo in company profile (manager only)
// If useDefault is true, sets logo to local public default URL (no Storage requests).
// If file is provided, uploads to Firebase Storage and stores the download URL.
export const updateCompanyLogo = async (
    managerId: string,
    file: File | null,
    useDefault: boolean
): Promise<{ logoUrl: string; logoFileName: string }> => {
    try {
        let logoUrl: string;
        let logoFileName: string;

        if (useDefault || !file) {
            logoUrl = DEFAULT_COMPANY_LOGO_URL;
            logoFileName = DEFAULT_COMPANY_LOGO_URL;
        } else {
            const uploadResult = await uploadCompanyLogo(managerId, file);
            logoUrl = uploadResult.url;
            logoFileName = uploadResult.fileName;
        }

        const companyDocRef = doc(db, 'companies', managerId);
        await updateDoc(companyDocRef, {
            logoUrl,
            logoFileName,
            updatedAt: serverTimestamp()
        });

        logger.log(`Company logo updated for manager ${managerId}`);
        return { logoUrl, logoFileName };
    } catch (error) {
        logger.error('Error updating company logo:', error);
        throw error;
    }
};

// Update member role in company (move between editors and users arrays)
export const updateMemberRoleInCompany = async (
    managerId: string,
    memberId: string,
    oldRole: 'user' | 'editor',
    newRole: 'user' | 'editor'
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);

        // Remove from old role array and add to new role array
        if (oldRole === 'editor' && newRole === 'user') {
            await updateDoc(companyDocRef, {
                editors: arrayRemove(memberId),
                users: arrayUnion(memberId),
                updatedAt: serverTimestamp()
            });
        } else if (oldRole === 'user' && newRole === 'editor') {
            await updateDoc(companyDocRef, {
                users: arrayRemove(memberId),
                editors: arrayUnion(memberId),
                updatedAt: serverTimestamp()
            });
        }

        logger.log(`Member ${memberId} role updated from ${oldRole} to ${newRole} in company`);
    } catch (error) {
        logger.error('Error updating member role in company:', error);
        throw error;
    }
};

// Remove member from company completely
export const removeMemberFromCompany = async (
    managerId: string,
    memberId: string,
    memberRole: 'user' | 'editor'
): Promise<void> => {
    try {
        const companyDocRef = doc(db, 'companies', managerId);

        // Remove from appropriate array based on role
        if (memberRole === 'editor') {
            await updateDoc(companyDocRef, {
                editors: arrayRemove(memberId),
                updatedAt: serverTimestamp()
            });
        } else {
            await updateDoc(companyDocRef, {
                users: arrayRemove(memberId),
                updatedAt: serverTimestamp()
            });
        }

        logger.log(`Member ${memberId} (${memberRole}) removed from company`);
    } catch (error) {
        logger.error('Error removing member from company:', error);
        throw error;
    }
};

// Get authentication error message based on language
export const getAuthErrorMessage = (errorCode: string, strings: any): string => {
    const errorMessages: { [ key: string ]: string } = {
        'auth/email-already-in-use': strings.FIREBASE_EMAIL_ALREADY_IN_USE,
        'auth/invalid-email': strings.FIREBASE_INVALID_EMAIL,
        'auth/operation-not-allowed': strings.FIREBASE_OPERATION_NOT_ALLOWED,
        'auth/weak-password': strings.FIREBASE_WEAK_PASSWORD,
        'auth/user-disabled': strings.FIREBASE_USER_DISABLED,
        'auth/user-not-found': strings.FIREBASE_USER_NOT_FOUND,
        'auth/wrong-password': strings.FIREBASE_WRONG_PASSWORD,
        'auth/invalid-credential': strings.FIREBASE_INVALID_CREDENTIAL,
        'auth/too-many-requests': strings.FIREBASE_TOO_MANY_REQUESTS,
        'auth/network-request-failed': strings.FIREBASE_NETWORK_REQUEST_FAILED,
        'auth/requires-recent-login': strings.FIREBASE_REQUIRES_RECENT_LOGIN
    };

    return errorMessages[ errorCode ] || strings.REGISTRATION_ERROR;
};

// Reauthenticate user before critical operations
export const reauthenticateUser = async (password: string): Promise<void> => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.email) {
            throw new Error('User not authenticated or email not available');
        }

        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(currentUser, credential);
        logger.log('User reauthenticated successfully');
    } catch (error) {
        logger.error('Error reauthenticating user:', error);
        throw convertFirebaseError(error as AuthError);
    }
};

// Change user password
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated');
        }

        // Reauthenticate user before changing password
        await reauthenticateUser(currentPassword);

        // Update password
        await updatePassword(currentUser, newPassword);
        logger.log('Password changed successfully');
    } catch (error) {
        logger.error('Error changing password:', error);
        throw convertFirebaseError(error as AuthError);
    }
};

// Delete user profile and account
export const deleteUserProfile = async (userId: string, password: string): Promise<void> => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.uid !== userId) {
            throw new Error('User not authenticated or user ID mismatch');
        }

        // Reauthenticate user before deleting account
        await reauthenticateUser(password);

        // Get user profile to check role and company
        const userProfile = await getUserProfile(userId);

        // If user is a manager with active subscription, cancel Stripe subscription
        if (userProfile && userProfile.role.toLowerCase() === 'manager' && userProfile.subscriptionId) {
            try {
                logger.log('Cancelling Stripe subscription before account deletion...');
                const cancelResult = await cancelSubscription(userProfile.subscriptionId);
                if (cancelResult.success) {
                    logger.log('Stripe subscription cancelled successfully');
                } else {
                    logger.warn('Failed to cancel Stripe subscription:', cancelResult.error);
                    // Continue with account deletion even if subscription cancellation fails
                }
            } catch (error) {
                logger.error('Error cancelling Stripe subscription:', error);
                // Continue with account deletion even if subscription cancellation fails
            }
        }

        // If user is a manager, delete company profile
        if (userProfile && userProfile.role.toLowerCase() === 'manager') {
            try {
                const companyDocRef = doc(db, 'companies', userId);
                const companyDoc = await getDoc(companyDocRef);
                if (companyDoc.exists()) {
                    await deleteDoc(companyDocRef);
                    logger.log('Company profile deleted successfully');
                }
            } catch (error) {
                logger.error('Error deleting company profile:', error);
                // Continue with user deletion even if company deletion fails
            }
        }

        // Delete user profile from Firestore first (before removing from companies)
        // This ensures the profile is deleted even if company removal fails
        try {
            const userDocRef = doc(db, 'users', userId);
            await deleteDoc(userDocRef);
            logger.log('User profile deleted from Firestore successfully');
        } catch (error) {
            logger.error('Error deleting user profile from Firestore:', error);
            throw error;
        }

        // Try to remove user from any companies they belong to (as editor or user)
        // This is not critical - if it fails, the user profile is already deleted
        // The manager can manually clean up the company later if needed
        try {
            const companiesRef = collection(db, 'companies');
            const editorQuery = query(companiesRef, where('editors', 'array-contains', userId));
            const userQuery = query(companiesRef, where('users', 'array-contains', userId));

            const [ editorSnapshot, userSnapshot ] = await Promise.all([
                getDocs(editorQuery),
                getDocs(userQuery)
            ]);

            // Remove from editors arrays (only if user has permission)
            for (const docSnapshot of editorSnapshot.docs) {
                try {
                    // Only try to update if user is the manager of this company
                    const companyData = docSnapshot.data();
                    if (companyData.managerId === userId) {
                        // User is manager, can update
                        await updateDoc(doc(db, 'companies', docSnapshot.id), {
                            editors: arrayRemove(userId),
                            updatedAt: serverTimestamp()
                        });
                    }
                    // If user is not manager, skip - they don't have permission to update
                } catch (updateError) {
                    logger.warn(`Could not remove user from company ${docSnapshot.id}:`, updateError);
                    // Continue with other companies
                }
            }

            // Remove from users arrays (only if user has permission)
            for (const docSnapshot of userSnapshot.docs) {
                try {
                    // Only try to update if user is the manager of this company
                    const companyData = docSnapshot.data();
                    if (companyData.managerId === userId) {
                        // User is manager, can update
                        await updateDoc(doc(db, 'companies', docSnapshot.id), {
                            users: arrayRemove(userId),
                            updatedAt: serverTimestamp()
                        });
                    }
                    // If user is not manager, skip - they don't have permission to update
                } catch (updateError) {
                    logger.warn(`Could not remove user from company ${docSnapshot.id}:`, updateError);
                    // Continue with other companies
                }
            }
        } catch (error) {
            logger.warn('Error removing user from companies (non-critical):', error);
            // This is not critical - user profile is already deleted
        }

        // Delete user account from Firebase Auth
        try {
            await deleteUser(currentUser);
            logger.log('User account deleted from Firebase Auth successfully');
        } catch (error) {
            logger.error('Error deleting user account from Firebase Auth:', error);
            throw error;
        }
    } catch (error) {
        logger.error('Error deleting user profile:', error);
        throw convertFirebaseError(error as AuthError);
    }
};

// Check if manager has active subscription
// Note: Subscription fields are stored only in UserProfile (manager document)
export const hasActiveSubscription = (profile: UserProfile | null): boolean => {
    if (!profile) {
        return false;
    }

    return profile.subscriptionStatus === 'active';
};

// Check if user's company manager has active subscription
export const checkManagerSubscriptionStatus = async (userId: string): Promise<{
    hasActiveSubscription: boolean;
    managerId: string | null;
    subscriptionStatus: 'active' | 'cancelled' | 'expired' | 'past_due' | 'incomplete' | null;
}> => {
    try {
        // Get company profile for user
        const companyProfile = await getCompanyProfileForUser(userId);

        if (!companyProfile) {
            return {
                hasActiveSubscription: false,
                managerId: null,
                subscriptionStatus: null
            };
        }

        // Get manager profile to check subscription status
        const managerProfile = await getUserProfile(companyProfile.managerId);

        if (!managerProfile) {
            return {
                hasActiveSubscription: false,
                managerId: companyProfile.managerId,
                subscriptionStatus: null
            };
        }

        const isActive = hasActiveSubscription(managerProfile);

        return {
            hasActiveSubscription: isActive,
            managerId: companyProfile.managerId,
            subscriptionStatus: managerProfile.subscriptionStatus || null
        };
    } catch (error) {
        logger.error('Error checking manager subscription status:', error);
        return {
            hasActiveSubscription: false,
            managerId: null,
            subscriptionStatus: null
        };
    }
};
